import { useEffect, useState } from "react";

/**
 * One toast at a time (Shared chrome board, turn 1 — a deliberate change from
 * the old FIFO queue): a second arrival replaces the first and restarts the
 * timer, and the replaced toast does not reappear.
 *
 * The holder renders the descriptor; the timer lives in the component, keyed by
 * `id`, so there is no shared interval and no element looked up by id.
 */
export type ToastKind = "message" | "visit" | "info" | "error";

export type ToastDescriptor = {
  id: number;
  kind: ToastKind;
  /** The member behind a message or visit toast. */
  member?: any;
  /** i18n id for the title line (member toasts), or literal text (info/error). */
  title?: string;
  message?: string;
  /** info: where the body tap goes. */
  onOpen?: () => void;
  /** error: the one retry action. */
  retry?: () => void;
};

class Toast {
  current: ToastDescriptor | null = null;
  listener: ((toast: ToastDescriptor | null) => void) | undefined;
  private nextId = 0;

  private show(toast: Omit<ToastDescriptor, "id">) {
    this.current = { ...toast, id: this.nextId++ };
    this.listener?.(this.current);
  }

  /** The socket hook's entry point — signature unchanged from the queue era. */
  display = (
    member: any,
    title: string,
    message: string,
    toastType: "messageReceived" | "profileVisit",
  ) => {
    this.show({
      kind: toastType === "messageReceived" ? "message" : "visit",
      member,
      title,
      message,
    });
  };

  info = (title: string, message?: string, onOpen?: () => void) =>
    this.show({ kind: "info", title, message, onOpen });

  error = (title: string, message?: string, retry?: () => void) =>
    this.show({ kind: "error", title, message, retry });

  close = () => {
    this.current = null;
    this.listener?.(null);
  };
}

const TOAST = new Toast();

export const closeCurrentToast = TOAST.close;
export const displayToast = TOAST.display;
export const displayInfoToast = TOAST.info;
export const displayErrorToast = TOAST.error;

export function useToast() {
  const [toast, setToast] = useState<ToastDescriptor | null>(TOAST.current);

  useEffect(() => {
    TOAST.listener = setToast;
    return () => {
      if (TOAST.listener === setToast) TOAST.listener = undefined;
    };
  }, []);

  return toast;
}
